/**
 * Tablet Registration Page
 * Standalone kiosk registration for AvePark tablets (1080x1920 portrait mode)
 * Features:
 * - 4-step registration flow without email verification
 * - Video background on welcome screen
 * - 5-minute inactivity timeout with 10-second warning
 * - Dynamic language selection
 * - Same consents and validation as main registration
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Eye, EyeOff, Loader2, Check,
  User, AlertCircle, CheckCircle, Users, X, AlertTriangle, Download, ArrowBigRight, ServerOff
} from 'lucide-react';
import { useTranslations } from '../context/TranslationsContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PhonePrefixSelect from '../components/PhonePrefixSelect';
import Logo from '../components/Logo';
import FlagIcon from '../components/FlagIcon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Inactivity timeout constants
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const WARNING_DURATION = 10 * 1000; // 10 seconds

// Helper function to calculate age
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Minor Alert Component
const MinorAlert = ({ t, lang }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-4 p-4 rounded-xl border-2 border-amber-500/50 bg-amber-500/10"
  >
    <div className="relative flex items-start gap-3 pr-10">
      <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />

      <div className="flex-1">
        <h4 className="font-bold text-amber-500 text-sm uppercase tracking-wide">
          {t('auth.minorAlert.title', 'Osoba niepełnoletnia')}
        </h4>

        <p className="text-sm text-muted-foreground mt-1">
          {t(
            'auth.minorAlert.description',
            'Jeśli jesteś niepełnoletni, rodzic/opiekun musi wypełnić oświadczenie.'
          )}
        </p>

        <p className="text-sm font-semibold text-muted-foreground mt-2">
          {t(
            'auth.minorAlert.docLocation',
            'Oświadczenie znajdziesz po prawej stronie kiosku rejestracyjnego.'
          )}
        </p>
      </div>

      <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none">
        <ArrowBigRight className="w-8 h-8 text-white animate-rightHint" />
      </div>
    </div>
  </motion.div>
);

const TabletRegistrationPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td } = useTranslations();

  // Local fitnet status check — more resilient for kiosk (Windows tablet).
  // Requires 2+ consecutive failures before switching to maintenance
  // once the service has been confirmed available at least once.
  const [serviceStatus, setServiceStatus] = useState('checking'); // 'checking' | 'available' | 'unavailable'
  const consecutiveFailsRef = useRef(0);
  const wasEverAvailableRef = useRef(false);

  useEffect(() => {
    const checkService = async () => {
      try {
        const res = await axios.get(`${API}/api/fitnet-status`, { timeout: 10000 });
        if (res.data?.available) {
          consecutiveFailsRef.current = 0;
          wasEverAvailableRef.current = true;
          setServiceStatus('available');
        } else {
          consecutiveFailsRef.current++;
          if (!wasEverAvailableRef.current || consecutiveFailsRef.current >= 2) {
            setServiceStatus('unavailable');
          }
        }
      } catch {
        consecutiveFailsRef.current++;
        if (!wasEverAvailableRef.current || consecutiveFailsRef.current >= 2) {
          setServiceStatus('unavailable');
        }
      }
    };

    checkService();
    const interval = setInterval(checkService, 30000);
    return () => clearInterval(interval);
  }, []);

  // Current step: 1 = Welcome, 2 = Registration form, 3 = Add person, 4 = Thank you
  const [step, setStep] = useState(1);

  // Languages
  const [languages, setLanguages] = useState([]);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Form data - same structure as AuthPages
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCode: '+48',
    birthDate: '',
    password: '',
    confirmPassword: '',
    gender: '',
    agreementTermsOfService: false,
    agreementSkateparkRules: false,
    agreementPrivacyPolicy: false,
    agreementMarketing: false,
    agreementNewsletter: false
  });

  // Subaccount data
  const [subaccountData, setSubaccountData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    phone: '',
    phoneCode: '+48'
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdUser, setCreatedUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [addedSubaccounts, setAddedSubaccounts] = useState([]);

  // Document modal state
  const [documentModal, setDocumentModal] = useState({ open: false, slug: '', title: '' });
  const [documentContent, setDocumentContent] = useState('');
  const [documentLoading, setDocumentLoading] = useState(false);

  // Inactivity timeout
  const [showWarning, setShowWarning] = useState(false);
  const [warningProgress, setWarningProgress] = useState(100);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const warningIntervalRef = useRef(null);

  const lang = i18n.language;

  // Translation helper - tries CMS first (tablet category), then i18n, then fallback
  const t = useCallback((key, fallback = null) => {
    // Check tablet-specific translation first
    const tabletKey = `tablet.${key}`;
    const tabletValue = td(tabletKey);
    if (tabletValue && tabletValue !== tabletKey) return tabletValue;

    // Then check CMS general
    const cmsValue = td(key);
    if (cmsValue && cmsValue !== key) return cmsValue;

    // Then i18n
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;

    return fallback || key;
  }, [td, ti18n]);

  // Localized path helper
  const lp = (path) => `/${lang}/${path}`;

  // Fetch languages on mount and set Polish as default
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API}/api/languages`);
        if (response.data?.length > 0) {
          setLanguages(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch languages:', err);
        setLanguages([
          { code: 'pl', nativeName: 'Polski', flag: '🇵🇱' },
          { code: 'en', nativeName: 'English', flag: '🇬🇧' }
        ]);
      }
    };
    fetchLanguages();

    // Force Polish as default for tablet registration
    // Only change if not already set or if set to browser default
    const savedLang = localStorage.getItem('i18nextLng');
    if (!savedLang || savedLang.length !== 2) {
      i18n.changeLanguage('pl');
    }
  }, [i18n]);

  // Password strength calculation - same as AuthPages
  const passwordStrength = useMemo(() => {
    const password = formData.password;
    if (!password) return { score: 0, label: '', color: '', checks: {} };

    const checks = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;
    let label = t('auth.passwordWeak', 'Słabe');
    let color = 'bg-red-500';

    if (score >= 4) {
      label = t('auth.passwordGood', 'Dobre');
      color = 'bg-blue-500';
    } else if (score >= 3) {
      label = t('auth.passwordMedium', 'Średnie');
      color = 'bg-yellow-500';
    }
    if (score === 5) {
      label = t('auth.passwordStrong', 'Silne');
      color = 'bg-green-500';
    }

    return { score, label, color, checks };
  }, [formData.password, t]);

  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  // Reset to welcome screen
  const resetToWelcome = useCallback(() => {
    setStep(1);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      phoneCode: '+48',
      birthDate: '',
      password: '',
      confirmPassword: '',
      gender: '',
      agreementTermsOfService: false,
      agreementSkateparkRules: false,
      agreementPrivacyPolicy: false,
      agreementMarketing: false,
      agreementNewsletter: false
    });
    setSubaccountData({
      firstName: '',
      lastName: '',
      birthDate: '',
      gender: '',
      phone: '',
      phoneCode: '+48'
    });
    setError('');
    setCreatedUser(null);
    setAuthToken(null);
    setAddedSubaccounts([]);
    setShowWarning(false);
    setWarningProgress(100);
  }, []);

  // Refs for stable references
  const stepRef = useRef(step);
  const showWarningRef = useRef(showWarning);

  // Update refs when state changes
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Handle user activity - reset timers
  const handleActivity = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);

    if (showWarningRef.current) {
      setShowWarning(false);
      setWarningProgress(100);
    }

    // Don't set timer on welcome screen
    if (stepRef.current === 1) return;

    inactivityTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setWarningProgress(100);

      const startTime = Date.now();
      warningIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / WARNING_DURATION) * 100);
        setWarningProgress(remaining);

        if (remaining <= 0) {
          clearInterval(warningIntervalRef.current);
        }
      }, 50);

      warningTimerRef.current = setTimeout(() => {
        resetToWelcome();
      }, WARNING_DURATION);
    }, INACTIVITY_TIMEOUT);
  }, [resetToWelcome]);

  // Set up activity listeners - only once on mount
  useEffect(() => {
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
    };
  }, [handleActivity]);

  // Start timer when step changes (not on welcome screen)
  useEffect(() => {
    if (step !== 1) {
      handleActivity();
    }
  }, [step, handleActivity]);

  // Auto-reset timer on thank you step
  useEffect(() => {
    if (step === 4) {
      const timer = setTimeout(resetToWelcome, 30000);
      return () => clearTimeout(timer);
    }
  }, [step, resetToWelcome]);

  // Change language
  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setLangMenuOpen(false);
    handleActivity();
  };

  // Open document in modal
  const openDocumentModal = async (slug, title) => {
    setDocumentModal({ open: true, slug, title });
    setDocumentLoading(true);
    setDocumentContent('');

    try {
      const response = await axios.get(`${API}/api/documents/${slug}?lang=${lang}`);
      // Content can be an object with language keys or a string
      const content = response.data.content;
      if (typeof content === 'object' && content !== null) {
        // Get content for current language or fallback to pl or first available
        setDocumentContent(content[lang] || content.pl || Object.values(content)[0] || '');
      } else {
        setDocumentContent(content || '');
      }
    } catch (err) {
      console.error('Failed to fetch document:', err);
      setDocumentContent(t('errors.documentLoadFailed', 'Nie udało się załadować dokumentu'));
    } finally {
      setDocumentLoading(false);
    }
  };

  // Close document modal
  const closeDocumentModal = () => {
    setDocumentModal({ open: false, slug: '', title: '' });
    setDocumentContent('');
  };

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Translate backend errors
  const translateError = (errorMsg) => {
    const errorMap = {
      'Missing required fields': t('errors.missingFields', 'Wypełnij wszystkie wymagane pola'),
      'Required consents must be accepted': t('errors.consentsRequired', 'Zaakceptuj wymagane zgody'),
      'Email is already registered': t('errors.emailExists', 'Ten email jest już zarejestrowany'),
      'User already exists': t('errors.emailExists', 'Ten email jest już zarejestrowany'),
      'Registration failed': t('errors.registrationFailed', 'Rejestracja nie powiodła się'),
      'Failed to create subaccount': t('errors.subaccountFailed', 'Nie udało się dodać osoby'),
      'Unauthorized': t('errors.unauthorized', 'Brak autoryzacji')
    };
    return errorMap[errorMsg] || errorMsg;
  };

  // Submit registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required consents
    if (!formData.agreementTermsOfService || !formData.agreementSkateparkRules || !formData.agreementPrivacyPolicy) {
      setError(t('auth.acceptAllRequired', 'Zaakceptuj wszystkie wymagane zgody'));
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError(t('auth.fillRequiredFields', 'Wypełnij wszystkie wymagane pola'));
      return;
    }

    if (!formData.gender) {
      setError(t('auth.selectGender', 'Wybierz płeć'));
      return;
    }

    if (!formData.birthDate) {
      setError(t('auth.birthDateRequired', 'Data urodzenia jest wymagana'));
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        sex: formData.gender,
        language: lang
      };
      delete payload.confirmPassword;
      delete payload.gender;

      const response = await axios.post(`${API}/api/auth/tablet-register`, payload, { timeout: 60000 });

      if (response.data.user) {
        setCreatedUser(response.data.user);
        setAuthToken(response.data.token);
        setStep(3);
      }
    } catch (err) {
      // If timeout or network error — account likely created, try auto-login
      if (err.code === 'ECONNABORTED' || !err.response) {
        try {
          const loginRes = await axios.post(`${API}/api/auth/login`, {
            email: formData.email,
            password: formData.password
          });
          if (loginRes.data.token) {
            setCreatedUser(loginRes.data.user);
            setAuthToken(loginRes.data.token);
            setStep(3);
            return;
          }
        } catch (loginErr) {
          // Login also failed — show friendly message with retry
        }
        setError(t('auth.registrationSlow', 'Rejestracja trwa dłużej niż zwykle. Spróbuj ponownie za chwilę.'));
      } else {
        const errorMsg = err.response?.data?.error || 'Registration failed';
        setError(translateError(errorMsg));
      }
    } finally {
      setLoading(false);
    }
  };

  // Add subaccount
  const handleAddSubaccount = async () => {
    setError('');

    if (!subaccountData.firstName.trim() || !subaccountData.lastName.trim() || !subaccountData.birthDate) {
      setError(t('auth.fillPersonData', 'Wypełnij imię, nazwisko i datę urodzenia'));
      return;
    }

    if (!subaccountData.gender) {
      setError(t('auth.selectGender', 'Wybierz płeć'));
      return;
    }

    if (!subaccountData.phone.trim()) {
      setError(t('auth.phoneRequired', 'Numer telefonu jest wymagany'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/subaccounts`, {
        ...subaccountData,
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
        timeout: 30000
      });
      // Success — remember the added person, clear form, stay on step 3
      setAddedSubaccounts(prev => [...prev, { firstName: subaccountData.firstName, lastName: subaccountData.lastName }]);
      setSubaccountData({ firstName: '', lastName: '', birthDate: '', gender: '', phone: '', phoneCode: '+48' });
    } catch (err) {
      // If timeout or network error but account might have been created
      if (err.code === 'ECONNABORTED' || !err.response) {
        // FitNet call might be slow — account likely created
        setAddedSubaccounts(prev => [...prev, { firstName: subaccountData.firstName, lastName: subaccountData.lastName }]);
        setSubaccountData({ firstName: '', lastName: '', birthDate: '', gender: '', phone: '', phoneCode: '+48' });
        return;
      }
      const errorMsg = err.response?.data?.error || 'Failed to create subaccount';
      setError(translateError(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  // Skip adding subaccount
  const handleSkipSubaccount = () => {
    setStep(4);
  };

  // Current language
  const currentLang = languages.find(l => l.code === i18n.language) || { code: 'pl', nativeName: 'Polski', flag: '🇵🇱' };

  // Render language selector
  const renderLanguageSelector = () => (
    <div className="absolute top-6 right-6 z-50">
      <button
        onClick={() => setLangMenuOpen(!langMenuOpen)}
        className="flex items-baseline gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-colors"
        data-testid="tablet-lang-selector"
      >
        <span className="text-xl"><FlagIcon code={currentLang.code} size={24} /></span>
        <span className="font-medium">{currentLang.code?.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {langMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 bg-black/90 backdrop-blur-xl rounded-2xl p-2 min-w-[160px] border border-white/10"
          >
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${lang.code === i18n.language ? 'bg-primary/30 text-white' : 'text-white/80 hover:bg-white/10'
                  }`}
              >
                <span className="text-xl"><FlagIcon code={lang.code} size={22} /></span>
                <span>{lang.nativeName}</span>
                {lang.code === i18n.language && <Check className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Render document modal
  const renderDocumentModal = () => (
    <AnimatePresence>
      {documentModal.open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-6"
          onClick={closeDocumentModal}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-card border border-border rounded-3xl w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-2xl font-bold">{documentModal.title}</h2>
              <button
                onClick={closeDocumentModal}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {documentLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{documentContent}</ReactMarkdown>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border">
              <button
                onClick={closeDocumentModal}
                className="w-full h-12 text-white font-bold rounded-full transition-all"
                style={{ backgroundColor: '#7C3AED' }}
              >
                {t('common.close', 'Zamknij')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Render inactivity warning
  const renderWarning = () => (
    <AnimatePresence>
      {showWarning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-8"
          onClick={handleActivity}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-card border border-border rounded-3xl p-12 max-w-lg text-center"
            onClick={e => e.stopPropagation()}
          >
            <AlertCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">{t('sessionExpiring', 'Sesja wygasa')}</h2>
            <p className="text-xl text-muted-foreground mb-8">
              {t('touchToContinue', 'Dotknij ekranu, aby kontynuować')}
            </p>

            <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-yellow-500 to-red-500"
                style={{ width: `${warningProgress}%` }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Step 1: Welcome screen
  const renderWelcome = () => (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden" data-testid="tablet-welcome">
      {/* Video background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="/videos/tablet-video.webm" type="video/webm" />
          <source src="/videos/tablet-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-8">
        {/* Logo */}
        <div className="mb-36">
          <Logo className="h-24 mx-auto" variant="light" />
        </div>

        <h1 className="text-6xl font-black text-white uppercase tracking-wider mb-2">
          {t('registration', 'REJESTRACJA')}
        </h1>

        <div className="w-24 h-1 bg-primary mx-auto mb-2" />

        <p className="text-xl text-white/80 max-w-lg mx-auto mb-36">
          {t('welcomeSubtitle', 'Wypełnij formularz rejestracyjny aby otrzymać status członka AvePark i rozpocząć zabawę!')}
        </p>

        <button
          onClick={() => setStep(2)}
          className="px-16 py-6 text-white text-2xl font-bold rounded-full uppercase tracking-wider transition-all transform hover:scale-105"
          style={{ backgroundColor: '#7C3AED' }}
          data-testid="tablet-start-btn"
        >
          {t('startRegistration', 'ROZPOCZNIJ REJESTRACJĘ')}
        </button>
      </div>

      {renderLanguageSelector()}
    </div>
  );

  // Step 2: Registration form
  const renderForm = () => (
    <div className="min-h-screen bg-background flex flex-col" data-testid="tablet-form">
      {renderLanguageSelector()}

      <div className="flex-1 flex flex-col justify-center py-8 px-6">
        <div className="max-w-2xl mx-auto w-full">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-4xl font-black uppercase tracking-wider mb-2">
              {t('registration', 'REJESTRACJA')}
            </h1>
            <div className="w-16 h-1 bg-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {t('formSubtitle', 'To konto jest jak główny klucz. Nie jest tylko dla jeżdżącego, a dla osoby, która będzie zarządzać profilami i opłacać wejścia.')}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleRegister} className="space-y-4" autoComplete="off">
            {/* Row: First name, Last name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-primary mb-1">{t('auth.firstName', 'Imię')} *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={e => handleChange('firstName', e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))}
                  className="w-full px-3 h-12 rounded-xl text-foreground"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                  placeholder={t('auth.firstName', 'Imię')}
                  autoComplete="off"
                  autoCapitalize="words"
                  data-testid="tablet-firstname"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-primary mb-1">{t('auth.lastName', 'Nazwisko')} *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={e => handleChange('lastName', e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))}
                  className="w-full px-3 h-12 rounded-xl text-foreground"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                  placeholder={t('auth.lastName', 'Nazwisko')}
                  autoComplete="off"
                  autoCapitalize="words"
                  data-testid="tablet-lastname"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-primary mb-1">{t('auth.email', 'Email')} *</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                className="w-full px-3 h-12 rounded-xl text-foreground"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                placeholder="email@example.com"
                autoComplete="off"
                data-testid="tablet-email"
              />
            </div>

            {/* Row: Password, Confirm password */}
            <div>
              <label className="block text-xs font-medium text-primary mb-1">{t('auth.password', 'Hasło')} *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={e => handleChange('password', e.target.value)}
                  className="w-full px-3 h-12 rounded-xl text-foreground pr-10"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  data-testid="tablet-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Row: Phone */}
            <div>
              <label className="block text-xs font-medium text-primary mb-1">{t('auth.phone', 'Numer telefonu')} *</label>
              <div className="flex gap-2">
                <PhonePrefixSelect
                  value={formData.phoneCode}
                  onChange={(v) => handleChange('phoneCode', v)}
                  className="w-28 h-12 rounded-xl"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => handleChange('phone', e.target.value.replace(/[^0-9\s]/g, ''))}
                  className="flex-1 px-3 h-12 rounded-xl text-foreground"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                  placeholder="123 456 789"
                  autoComplete="off"
                  inputMode="numeric"
                  pattern="[0-9\s]*"
                  data-testid="tablet-phone"
                />
              </div>
            </div>

            {/* Row: Birth date, Gender */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-primary mb-1">{t('auth.birthDate', 'Data urodzenia')} *</label>
                <input
                  type="date"
                  value={formData.birthDate}
                  onChange={e => handleChange('birthDate', e.target.value)}
                  ref={el => { if (el && !el._pickerBound) { el._pickerBound = true; el.addEventListener('click', function() { this.showPicker?.(); }); } }}
                  className="w-full px-3 h-12 rounded-xl text-foreground"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                  autoComplete="off"
                  data-testid="tablet-birthdate"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-primary mb-1">{t('auth.gender', 'Płeć')} *</label>
                <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                  <SelectTrigger
                    className="w-full h-12 rounded-xl"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                    data-testid="tablet-gender"
                  >
                    <SelectValue placeholder={t('common.select', 'Wybierz')} />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                    <SelectItem value="male">{t('auth.male', 'Mężczyzna')}</SelectItem>
                    <SelectItem value="female">{t('auth.female', 'Kobieta')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Minor Alert - show if under 18 */}
            {formData.birthDate && calculateAge(formData.birthDate) < 18 && (
              <MinorAlert t={t} lang={lang} />
            )}

            {/* Consents */}
            <div className="space-y-2 pt-3 border-t border-white/10">
              <p className="text-md font-medium text-muted-foreground mb-2">
                {t('auth.consents', 'Zgody')}
              </p>

              {/* Terms of Service */}
              <label className="flex items-start gap-2 cursor-pointer items-baseline">
                <input
                  type="checkbox"
                  checked={formData.agreementTermsOfService}
                  onChange={e => handleChange('agreementTermsOfService', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                />
                <span className="text-md text-muted-foreground">
                  {t('auth.iAccept', 'Akceptuję ')}<button type="button" onClick={() => openDocumentModal('regulamin-serwisu', t('auth.termsOfService', 'Regulamin Serwisu'))} className="text-primary underline">{t('auth.termsOfService', 'Regulamin Serwisu')}</button>{' '}<span className="text-primary">*</span>
                </span>
              </label>

              {/* Skatepark Rules */}
              <label className="flex items-start gap-2 cursor-pointer items-baseline">
                <input
                  type="checkbox"
                  checked={formData.agreementSkateparkRules}
                  onChange={e => handleChange('agreementSkateparkRules', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                />
                <span className="text-md text-muted-foreground">
                  {t('auth.iAccept', 'Akceptuję ')}<button type="button" onClick={() => openDocumentModal('regulamin-skateparku', t('auth.skateparkRules', 'Regulamin Skateparku'))} className="text-primary underline">{t('auth.skateparkRules', 'Regulamin Skateparku')}</button>{' '}<span className="text-primary">*</span>
                </span>
              </label>

              {/* Privacy Policy */}
              <label className="flex items-start gap-2 cursor-pointer items-baseline">
                <input
                  type="checkbox"
                  checked={formData.agreementPrivacyPolicy}
                  onChange={e => handleChange('agreementPrivacyPolicy', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                />
                <span className="text-md text-muted-foreground">
                  {t('auth.iAccept', 'Akceptuję ')}<button type="button" onClick={() => openDocumentModal('polityka-prywatnosci', t('auth.privacyPolicy', 'Polityka Prywatności'))} className="text-primary underline">{t('auth.privacyPolicy', 'Politykę prywatności')}</button>{' '}<span className="text-primary">*</span>
                </span>
              </label>

              {/* Marketing */}
              <label className="flex items-start gap-2 cursor-pointer items-baseline">
                <input
                  type="checkbox"
                  checked={formData.agreementMarketing}
                  onChange={e => handleChange('agreementMarketing', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                />
                <span className="text-md text-muted-foreground">
                  {t('auth.marketing', 'Wyrażam zgodę na otrzymywanie informacji handlowych')}{' '}<span className="text-muted-foreground/60">{t('auth.optionalConsent', '(opcjonalne)')}</span>
                </span>
              </label>

              {/* Newsletter */}
              <label className="flex items-start gap-2 cursor-pointer items-baseline">
                <input
                  type="checkbox"
                  checked={formData.agreementNewsletter}
                  onChange={e => handleChange('agreementNewsletter', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                />
                <span className="text-md text-muted-foreground">
                  {t('auth.newsletterOptional', 'Chcę otrzymywać newsletter z nowościami i promocjami')}{' '}<span className="text-muted-foreground/60">{t('auth.optionalConsent', '(opcjonalne)')}</span>
                </span>
              </label>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 text-white text-lg font-bold rounded-full uppercase tracking-wider transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-4"
              style={{ backgroundColor: '#7C3AED' }}
              data-testid="tablet-register-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.registering', 'Rejestracja...')}
                </>
              ) : (
                t('auth.register', 'ZAREJESTRUJ')
              )}
            </button>
          </form>
        </div>
      </div>

      {renderWarning()}
      {renderDocumentModal()}
    </div>
  );

  // Step 3: Add person
  const renderAddPerson = () => (
    <div className="min-h-screen bg-background py-8 px-6 flex flex-col items-center justify-center" data-testid="tablet-add-person">
      {renderLanguageSelector()}

      <div className="max-w-xl w-full text-center">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>

        <h1 className="text-3xl font-bold mb-3">{t('accountCreated', 'Konto utworzone!')}</h1>
        <p className="text-muted-foreground mb-6">
          {t('wantToAddPerson', 'Czy chcesz dodać dodatkową osobę do swojego konta? (np. dziecko)')}
        </p>

        {/* List of already added subaccounts */}
        {addedSubaccounts.length > 0 && (
          <div className="mb-4 space-y-2">
            {addedSubaccounts.map((sub, i) => (
              <div key={i} className="flex items-center gap-2 justify-center text-green-400 text-sm">
                <Check className="w-4 h-4" />
                <span>{sub.firstName} {sub.lastName}</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Subaccount form */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6 text-left">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {addedSubaccounts.length > 0
                ? t('addNextPerson', 'Dodaj kolejną osobę')
                : t('addPerson', 'Dodaj osobę')}
            </h2>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={subaccountData.firstName}
                onChange={e => setSubaccountData({ ...subaccountData, firstName: e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1) })}
                className="px-3 h-12 bg-white/5 border border-border rounded-xl text-foreground"
                placeholder={t('auth.firstName', 'Imię')}
                autoComplete="off"
                autoCapitalize="words"
              />
              <input
                type="text"
                value={subaccountData.lastName}
                onChange={e => setSubaccountData({ ...subaccountData, lastName: e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1) })}
                className="px-3 h-12 bg-white/5 border border-border rounded-xl text-foreground"
                placeholder={t('auth.lastName', 'Nazwisko')}
                autoComplete="off"
                autoCapitalize="words"
              />
            </div>

            {/* Phone number */}
            <div>
              <label className="block text-xs font-medium text-primary mb-1">{t('auth.phone', 'Numer telefonu')} *</label>
              <div className="flex gap-2">
                <PhonePrefixSelect
                  value={subaccountData.phoneCode}
                  onChange={(v) => setSubaccountData({ ...subaccountData, phoneCode: v })}
                  className="w-28 h-12 bg-white/5 border border-border rounded-xl"
                />
                <input
                  type="tel"
                  value={subaccountData.phone}
                  onChange={e => setSubaccountData({ ...subaccountData, phone: e.target.value.replace(/[^0-9\s]/g, '') })}
                  className="flex-1 px-3 h-12 bg-white/5 border border-border rounded-xl text-foreground"
                  placeholder="123 456 789"
                  autoComplete="off"
                  inputMode="numeric"
                  pattern="[0-9\s]*"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                value={subaccountData.birthDate}
                onChange={e => setSubaccountData({ ...subaccountData, birthDate: e.target.value })}
                ref={el => { if (el && !el._pickerBound) { el._pickerBound = true; el.addEventListener('click', function() { this.showPicker?.(); }); } }}
                className="px-3 h-12 bg-white/5 border border-border rounded-xl text-foreground"
              />
              <Select value={subaccountData.gender} onValueChange={(v) => setSubaccountData({ ...subaccountData, gender: v })}>
                <SelectTrigger
                  className="h-12 rounded-xl"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid #333' }}
                >
                  <SelectValue placeholder={t('auth.gender', 'Płeć')} />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                  <SelectItem value="male">{t('auth.male', 'Mężczyzna')}</SelectItem>
                  <SelectItem value="female">{t('auth.female', 'Kobieta')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Minor Alert for subaccount */}
            {subaccountData.birthDate && calculateAge(subaccountData.birthDate) < 18 && (
              <MinorAlert t={t} lang={lang} />
            )}

            <button
              onClick={handleAddSubaccount}
              disabled={loading}
              className="w-full h-12 text-white font-bold rounded-full transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#7C3AED' }}
              data-testid="tablet-add-subaccount-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <User className="w-4 h-4" />
                  {t('addPerson', 'Dodaj osobę')}
                </>
              )}
            </button>
          </div>
        </div>

        <button
          onClick={handleSkipSubaccount}
          className="text-muted-foreground hover:text-foreground transition-colors text-lg"
          data-testid="tablet-finish-btn"
        >
          {addedSubaccounts.length > 0
            ? t('finishRegistration', 'Zakończ rejestrację') + ' →'
            : t('skipForNow', 'Pomiń na razie') + ' →'}
        </button>
      </div>

      {renderWarning()}
    </div>
  );

  // Step 4: Thank you
  const renderThankYou = () => (
    <div className="min-h-screen bg-background flex items-center justify-center p-8" data-testid="tablet-thank-you">
      <div className="text-center max-w-lg">
        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center mx-auto mb-8">
          <CheckCircle className="w-14 h-14 text-white" />
        </div>

        <h1 className="text-5xl font-black uppercase mb-6">
          {t('thankYou', 'Dziękujemy!')}
        </h1>

        <p className="text-xl text-muted-foreground mb-12">
          {t('registrationComplete', 'Twoja rejestracja została zakończona. Możesz teraz korzystać ze wszystkich usług AvePark.')}
        </p>

        <button
          onClick={resetToWelcome}
          className="px-12 py-5 bg-white/10 hover:bg-white/20 text-white text-xl font-bold rounded-full transition-all"
        >
          {t('backToStart', 'Powrót do ekranu głównego')}
        </button>

        <p className="text-sm text-muted-foreground mt-8">
          {t('autoReset', 'Ekran automatycznie powróci do strony głównej za 30 sekund')}
        </p>
      </div>
    </div>
  );

  // Maintenance / offline screen for when FitNet or services are unavailable
  if (serviceStatus === 'checking') {
    return (
      <div className="tablet-registration w-full flex items-center justify-center" style={{ minHeight: '100vh', overflow: 'hidden', background: '#0a0a0a' }}>
        {renderLanguageSelector()}
        <div className="text-center px-8">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
          <p className="text-xl text-white/60">{t('common.loading', 'Ładowanie...')}</p>
        </div>
      </div>
    );
  }

  if (serviceStatus === 'unavailable') {
    return (
      <div className="tablet-registration w-full flex items-center justify-center relative" style={{ minHeight: '100vh', overflow: 'hidden', background: '#0a0a0a' }} data-testid="tablet-maintenance">
        {renderLanguageSelector()}
        <div className="text-center px-8 max-w-xl">
          <div className="w-24 h-24 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-8">
            <ServerOff className="w-12 h-12 text-orange-400" />
          </div>
          <h1 className="text-4xl font-black text-white uppercase tracking-wider mb-4">
            {t('maintenance.title', 'Przerwa techniczna')}
          </h1>
          <div className="w-16 h-1 bg-orange-500 mx-auto mb-6" />
          <p className="text-xl text-white/70 mb-8">
            {td('maintenance.description') !== 'maintenance.description'
              ? td('maintenance.description')
              : t('maintenance.tabletDescription', 'System rejestracji jest chwilowo niedostępny. Przepraszamy za utrudnienia.')}
          </p>
          <div className="flex items-center justify-center gap-3 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">{t('maintenance.autoRefresh', 'Automatyczne sprawdzanie co 30 sekund...')}</span>
          </div>
        </div>
      </div>
    );
  }

  // Render current step
  return (
    <div className="tablet-registration w-full" style={{ minHeight: '100vh', overflow: 'hidden' }}>
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="welcome" className="w-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {renderWelcome()}
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="form" className="w-full" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
            {renderForm()}
          </motion.div>
        )}
        {step === 3 && (
          <motion.div key="add-person" className="w-full" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
            {renderAddPerson()}
          </motion.div>
        )}
        {step === 4 && (
          <motion.div key="thank-you" className="w-full" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
            {renderThankYou()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TabletRegistrationPage;
